import type { ComponentProps } from 'react';
import {
  asWidgetProps,
  BucketFormikForm,
  asTrackingPayload,
  BucketFormikInput,
} from 'Src/types/widget-helpers';
import React from 'react';
import accounting from 'accounting';
import { Formik } from 'formik';
import { useAppContext } from 'Context/ReactAppContext';
import logger from 'Logger';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { getCurrency } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { Button } from 'Components/frameworks/button/Button';
import { Legend } from 'Components/frameworks/formik/formik-input/Legend';
import { getPointsFromAmount } from './GreatMembersPointsSimulatorForm.utils';
import { getValidationSchema } from './GreatMembersPointsSimulatorForm.validationSchema';
type Props = {
  currencyCode: string;
  onSubmit: (points: number) => void;
};
export const GreatMembersPointsSimulatorForm = ({ currencyCode, onSubmit }: Props) => {
  const appContext = useAppContext();
  const { decimal, symbol, thousand } = getCurrency(appContext.locale, currencyCode);
  const { trackClickEvent } = useTrackEvent();
  const onSubmitForm = async ({ amount, duration }) => {
    try {
      const data = await getPointsFromAmount({
        amount,
        appContext,
        duration,
      });
      const points = data?.loyaltyProgramSimulation?.points || 0;
      const formattedPoints = accounting.formatNumber(points, {
        decimal,
        thousand,
      });
      trackClickEvent(
        TRACK_EVENT_NAMES.CLICK_CA_GM,
        asTrackingPayload({
          detail_click: 'see_results',
        }),
      );
      onSubmit(formattedPoints);
    } catch (err) {
      logger.error(err);
    }
  };
  return (
    <Formik
      {...asWidgetProps<ComponentProps<typeof Formik>>({
        initialValues: {
          amount: 0,
          duration: '7',
        },
        onSubmit: onSubmitForm,
        validationSchema: getValidationSchema(),
      })}
    >
      <BucketFormikForm
        {...asWidgetProps<ComponentProps<typeof BucketFormikForm>>({
          className: 'flex flex-col',
        })}
      >
        <div className="flex flex-col gap-20 lg:flex-row">
          <div className="lg:min-w-340">
            <BucketFormikInput
              {...asWidgetProps<ComponentProps<typeof BucketFormikInput>>({
                hideErrorMessage: true,
                label: t('customerAccount.greatMember.pointsSection.simulator.totalCostLabel'),
                labelAdditionalInfo: `(${symbol})`,
                placeholder: '800',
                name: 'amount',
                type: 'number',
              })}
            />
          </div>
          <fieldset className="flex flex-1 flex-col justify-start gap-x-10 lg:flex-row">
            <Legend
              {...asWidgetProps<ComponentProps<typeof Legend>>({
                className: 'mb-5 w-full flex-none',
                label: t('common.durationLabel'),
              })}
            />
            <BucketFormikInput
              {...asWidgetProps<ComponentProps<typeof BucketFormikInput>>({
                hideErrorMessage: true,
                label: t('customerAccount.greatMember.pointsSection.simulator.oneNightLabel'),
                name: 'duration',
                type: 'radio',
                value: '1',
              })}
            />
            <BucketFormikInput
              {...asWidgetProps<ComponentProps<typeof BucketFormikInput>>({
                hideErrorMessage: true,
                label: t('customerAccount.greatMember.pointsSection.simulator.up7NightsLabel'),
                name: 'duration',
                type: 'radio',
                value: '7',
              })}
            />
            <BucketFormikInput
              {...asWidgetProps<ComponentProps<typeof BucketFormikInput>>({
                hideErrorMessage: true,
                label: t('customerAccount.greatMember.pointsSection.simulator.more7NightsLabel'),
                name: 'duration',
                type: 'radio',
                value: '8',
              })}
            />
          </fieldset>
        </div>
        <Button
          {...asWidgetProps<ComponentProps<typeof Button>>({
            className: 'self-center md:self-end',
            type: 'submit',
          })}
        >
          {t('common.seeResult')}
        </Button>
      </BucketFormikForm>
    </Formik>
  );
};
